'use client'
import { type FC, type ReactNode } from 'react'
import { Box, Divider, Flex, HStack, Text, VStack } from '@chakra-ui/react'
import { GrandHeading, GrandImage } from 'ui'
import { overrideText } from 'ui/lib/overrides'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import { type Language, type OrderAddressDTO, type OrderDTO, type OrderItemDTO } from 'ecosystem'
import {
  formatAmount,
  getDocumentLanguage,
  getOrderItemsDiscount,
  getProductImages,
  resolveCountryName
} from 'shared-utils'

/**
 * Width of the confirmation page's content column.
 *
 * Exported so anything rendered alongside the summary card — the Briqpay
 * pending-payment alert, for one — lines up with it instead of running the full width of
 * the section.
 */
export const CONFIRMATION_CONTENT_MAX_W = '640px'

// Chakra's `Text` rather than `ui`'s `GrandText`: the latter hardcodes `w="100%"`, which
// inside these flex rows makes every label and price claim the full row width and fight
// the layout — the product name ends up squeezed and wrapped.

export interface ConfirmationOrderSummaryOverrides {
  confirmationSummaryHeading?: string
  confirmationSummaryQuantity?: string
  confirmationSummaryShipping?: string
  confirmationSummaryShippingFree?: string
  confirmationSummaryDiscount?: string
  confirmationSummaryVat?: string
  confirmationSummaryTotal?: string
  confirmationSummaryReceiptEmail?: string
  confirmationSummaryShippingAddress?: string
  confirmationSummaryPickup?: string
  confirmationSummaryTracking?: string
  confirmationSummaryPaymentMethod?: string
}

interface ConfirmationOrderSummaryProps {
  order: OrderDTO
  /**
   * How the shopper actually paid. Worth showing for an orchestrated provider, where the
   * PSP differs per order and neither the merchant nor support can infer it.
   */
  paymentMethod?: string
  /**
   * Where the receipt goes.
   *
   * Defaults to the order's customer, but that is empty on the *first* confirmation
   * render: provisioning creates the Customer and the PUT response does not echo it back,
   * so it only appears once the page is reloaded and the order is re-fetched. Callers
   * that hold the payment session can pass the address from there instead.
   */
  email?: string
}

const THUMB_SIZE = 64

const Row: FC<{ label: string; value: string; strong?: boolean }> = ({ label, value, strong }) => (
  <HStack align="baseline" justify="space-between" w="100%">
    <Text color={strong ? undefined : 'text.mild'} fontWeight={strong ? 'semibold' : undefined}>
      {label}
    </Text>
    <Text
      fontSize={strong ? 'lg' : undefined}
      fontWeight={strong ? 'semibold' : undefined}
      whiteSpace="nowrap"
    >
      {value}
    </Text>
  </HStack>
)

const OrderLine: FC<{ item: OrderItemDTO; money: (value: number | undefined) => string }> = ({
  item,
  money
}) => {
  const { mainImageUrl } = getProductImages(item.product.medias)
  const overrides = useOverridesContext<keyof ConfirmationOrderSummaryOverrides>()
  const isDiscounted = (item.originalUnitPrice ?? 0) > item.unitPrice

  return (
    <HStack align="center" gap={4} w="100%">
      {mainImageUrl ? (
        <Box
          flexShrink={0}
          h={`${THUMB_SIZE}px`}
          overflow="hidden"
          position="relative"
          w={`${THUMB_SIZE}px`}
        >
          <GrandImage
            alt={item.product.name}
            fill
            sizes={`${THUMB_SIZE}px`}
            src={mainImageUrl}
            objectFit="contain"
          />
        </Box>
      ) : null}

      {/* `flex="1"` makes this column absorb the free space so the price is pushed to
          the right edge, and `minW={0}` lets it shrink inside the flex row rather than
          overflowing. A `Spacer` here would take the free space for itself and squeeze
          the name into a wrapped column instead. */}
      <VStack align="flex-start" flex="1" gap={0} minW={0}>
        <Text fontWeight="medium" noOfLines={2}>
          {item.product.name}
        </Text>
        <Text color="text.mild" fontSize="sm">
          {`${overrideText('Antal', overrides?.confirmationSummaryQuantity)}: ${item.quantity}`}
        </Text>
      </VStack>

      <VStack align="flex-end" flexShrink={0} gap={0}>
        <Text fontWeight="medium" whiteSpace="nowrap">
          {money(item.unitPrice * item.quantity)}
        </Text>
        {/* The price before the code and the quantity discount. */}
        {isDiscounted ? (
          <Text as="s" color="text.mild" fontSize="sm" whiteSpace="nowrap">
            {money(item.originalUnitPrice * item.quantity)}
          </Text>
        ) : null}
      </VStack>
    </HStack>
  )
}

/**
 * The delivery address on one line, with the country spelled out.
 *
 * A receipt is skim-read, so this favours a single scannable line over a postal-format
 * block. `attention` leads only when it names a person; for a pickup point it is a
 * collection instruction ("Ask Hans-Adam"), which belongs under the location name rather
 * than in place of a recipient.
 */
const formatAddressLine = (address: OrderAddressDTO | undefined, locale: Language) => {
  if (!address) {
    return null
  }

  const parts = [
    address.address1,
    address.address2,
    [address.postalCode, address.postalRegion].filter(Boolean).join(' '),
    resolveCountryName(address.country, locale)
  ]

  return parts.filter((part) => !!part?.trim()).join(', ') || null
}

const InfoBlock: FC<{ title: string; children: ReactNode }> = ({ title, children }) => (
  <VStack align="flex-start" gap={1}>
    <Text
      color="text.mild"
      fontSize="xs"
      fontWeight="semibold"
      letterSpacing="wide"
      textTransform="uppercase"
    >
      {title}
    </Text>
    {/* `children` arrives as an array. Rendering it directly would nest that array inside
        this stack's own children array, where the caller's implicit positional keys no
        longer apply and React asks for explicit ones. Giving it its own stack keys it
        (Chakra runs `Children.toArray`) and tightens the spacing between the lines. */}
    <VStack align="flex-start" gap={0}>
      {children}
    </VStack>
  </VStack>
)

/**
 * Order summary for the confirmation page, rendered from our own order rather than a
 * payment provider's receipt widget.
 *
 * Klarna, Svea and Qliro each render totals inside their own iframe, so nothing like this
 * was needed before. Briqpay does not: its `thankYouSnippet` is deprecated — Briqpay's own
 * support confirmed no merchant uses it and that the merchant is expected to build the
 * confirmation page — so the shopper would otherwise see nothing but a thank-you line.
 *
 * Reads only `OrderDTO`, so it is provider-agnostic and reusable by the invoice flow.
 */
export const ConfirmationOrderSummary: FC<ConfirmationOrderSummaryProps> = ({
  order,
  paymentMethod,
  email: emailOverride
}) => {
  const overrides = useOverridesContext<keyof ConfirmationOrderSummaryOverrides>()
  const currency = order.currency ?? ''
  const money = (value: number | undefined) =>
    value === undefined ? '-' : `${formatAmount(value)} ${currency}`

  const items = order.items ?? []
  const shippingCost = order.shippingCost ?? 0
  // From the items: a storefront order carries every discount on them, not in `orderDiscount`.
  const discount = getOrderItemsDiscount(order)
  const pickupName = order.shippingInfo?.pickupLocationName
  const trackingUrl = order.shippingInfo?.trackingUrl
  const email = emailOverride ?? order.customer?.email
  const deliveryLine = formatAddressLine(order.shippingAddress, getDocumentLanguage())

  return (
    <Box
      bg="background.default"
      borderColor="background.mild"
      borderRadius="xl"
      borderWidth="1px"
      maxW={CONFIRMATION_CONTENT_MAX_W}
      mx="auto"
      overflow="hidden"
      w="100%"
    >
      <Box px={{ base: 4, md: 6 }} py={4}>
        <GrandHeading
          fontSize="lg"
          headingTag="h3"
          title={overrideText('Din beställning', overrides?.confirmationSummaryHeading)}
        />
      </Box>

      <Divider />

      <VStack align="stretch" gap={4} px={{ base: 4, md: 6 }} py={5} w="100%">
        {items.map((item, index) => (
          <OrderLine item={item} key={`${item.product.id}-${index}`} money={money} />
        ))}
      </VStack>

      <Divider />

      <VStack align="stretch" bg="background.surface" gap={2} px={{ base: 4, md: 6 }} py={5}>
        {discount ? (
          <Row
            label={overrideText('Rabatt', overrides?.confirmationSummaryDiscount)}
            value={`-${money(discount)}`}
          />
        ) : null}
        <Row
          label={overrideText('Frakt', overrides?.confirmationSummaryShipping)}
          value={
            shippingCost
              ? money(shippingCost)
              : overrideText('Fri frakt', overrides?.confirmationSummaryShippingFree)
          }
        />
        {order.orderTaxAmount ? (
          <Row
            label={overrideText('Varav moms', overrides?.confirmationSummaryVat)}
            value={money(order.orderTaxAmount)}
          />
        ) : null}
        <Divider my={1} />
        <Row
          label={overrideText('Totalt', overrides?.confirmationSummaryTotal)}
          strong
          value={money(order.orderAmount)}
        />
      </VStack>

      <Divider />

      <VStack align="stretch" gap={5} px={{ base: 4, md: 6 }} py={5}>
        {paymentMethod || email || trackingUrl ? (
          <VStack align="stretch" gap={2}>
            {paymentMethod ? (
              <Row
                label={overrideText('Betalsätt', overrides?.confirmationSummaryPaymentMethod)}
                value={paymentMethod}
              />
            ) : null}
            {/* Where the receipt goes — the shopper's last chance to spot a typo. */}
            {email ? (
              <Row
                label={overrideText('E-post', overrides?.confirmationSummaryReceiptEmail)}
                value={email}
              />
            ) : null}
            {trackingUrl ? (
              <Flex justify="flex-end">
                <Box
                  as="a"
                  color="primary"
                  fontWeight="medium"
                  href={trackingUrl}
                  rel="noreferrer"
                  target="_blank"
                  textDecoration="underline"
                >
                  {overrideText('Spåra din försändelse', overrides?.confirmationSummaryTracking)}
                </Box>
              </Flex>
            ) : null}
          </VStack>
        ) : null}

        {deliveryLine ? (
          <InfoBlock
            title={
              pickupName
                ? overrideText('Uthämtningsställe', overrides?.confirmationSummaryPickup)
                : overrideText('Leveransadress', overrides?.confirmationSummaryShippingAddress)
            }
          >
            {pickupName ? <Text fontWeight="medium">{pickupName}</Text> : null}
            <Text color="text.mild">{deliveryLine}</Text>
            {order.shippingAddress?.attention ? (
              <Text color="text.mild" fontSize="sm">
                {order.shippingAddress.attention}
              </Text>
            ) : null}
          </InfoBlock>
        ) : null}
      </VStack>
    </Box>
  )
}
